import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { pockRequestsApi, type PockRequestItemPayload } from "@/features/orders/api/pockRequests";
import apiClient from "@/api/client";

export function usePockRequests(orderId?: string, status?: string) {
  const params: Record<string, string> = {};
  if (orderId) params.order = orderId;
  if (status) params.status = status;
  return useQuery({
    queryKey: ["pock-requests", params],
    queryFn: () => pockRequestsApi.list(params),
    enabled: !!orderId,
  });
}

export function useSubmitPockRequest(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { note?: string; proposed_settlement_pct?: string; proposed_is_final?: boolean; items: PockRequestItemPayload[] }) =>
      pockRequestsApi.create({ order: orderId, ...data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pock-requests"] });
    },
  });
}

export function useApprovePockRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment, approved_settlement_pct, is_final }: { id: string; comment?: string; approved_settlement_pct?: string; is_final?: boolean }) =>
      pockRequestsApi.approve(id, comment, approved_settlement_pct, is_final),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pock-requests"] });
    },
  });
}

export function useRejectPockRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      pockRequestsApi.reject(id, comment),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pock-requests"] });
    },
  });
}

export function useWithdrawPockRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pockRequestsApi.withdraw(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pock-requests"] });
    },
  });
}

type ContractorProtocolListItem = {
  id: string;
  is_withdrawn: boolean;
  accepted_by_contractor: boolean | null;
  invoices: { status: string }[];
};

export function usePendingProtocolsCount(enabled = true) {
  return useQuery({
    // Shared key with ContractorProtocolsPage — one request, two subscribers.
    queryKey: ["contractor-protocols"],
    queryFn: () =>
      apiClient
        .get<{ data: ContractorProtocolListItem[] }>("/protocols/")
        .then((r) => r.data.data),
    enabled,
    select: (data) => data.filter((p) => !p.is_withdrawn && p.accepted_by_contractor === null).length,
  });
}

export function useProtocolsNeedingInvoiceCount(enabled = true) {
  return useQuery({
    queryKey: ["contractor-protocols"],
    queryFn: () =>
      apiClient
        .get<{ data: ContractorProtocolListItem[] }>("/protocols/")
        .then((r) => r.data.data),
    enabled,
    select: (data) =>
      data.filter(
        (p) =>
          !p.is_withdrawn &&
          p.accepted_by_contractor === true &&
          (p.invoices ?? []).filter((inv) => inv.status !== "withdrawn").length === 0,
      ).length,
  });
}
